import { ProcessingPurposeData, RetentionOverrideInput } from '../@types';
import { formatISODuration } from './uiHelpers';

// Retention overrides are keyed by USER block status, so they only ever live on
// the users data collection. The collection list itself comes from the backend
// (/fleets/privacy/collectionNames), so this value is a deliberate coupling —
// keep it here as the single place that needs changing if it is ever renamed.
export const USERS_COLLECTION = 'users';

export const REGULAR_USERS_TEXT_KEY =
  'fleetadmin.purpose.retention.regularusers';
export const BLOCKED_USERS_TEXT_KEY =
  'fleetadmin.purpose.retention.blockedusers';

// A grouped purpose spans one backend purpose per collection. Overrides are
// written only to the users one, so every read must resolve it this way rather
// than reading the purpose the admin happened to click.
export const getUsersPurpose = (
  purposes?: ProcessingPurposeData[]
): ProcessingPurposeData | undefined =>
  purposes?.find((purpose) => purpose?.collectionName === USERS_COLLECTION);

// Stable serialisation for diffing. Key order is fixed, blockReasons is sorted
// on a COPY (the source lives in the redux store), and absent/''/null all
// collapse to null so a server rule and a locally built one compare equal.
// The outer list is deliberately NOT sorted — its order is the rule precedence
// the backend reports back as effectiveRetentionRuleIndex.
export const serialiseOverrides = (list?: RetentionOverrideInput[]) =>
  JSON.stringify(
    (list || []).map((override) => ({
      blocked: !!override?.blocked,
      blockReasons: [...(override?.blockReasons || [])].sort(),
      minDuration: override?.minDuration || null,
      maxDuration: override?.maxDuration || null,
    }))
  );

// The text keys describing who a rule targets: regular users, every blocked
// user, or one key per selected block reason.
export const getOverrideAudienceKeys = (
  override: RetentionOverrideInput
): string[] => {
  if (override?.blocked !== true) return [REGULAR_USERS_TEXT_KEY];

  const reasons = (override?.blockReasons || []).filter(Boolean);
  return reasons.length
    ? reasons.map((reason) => `fleetadmin.blockReasons.${reason}`)
    : [BLOCKED_USERS_TEXT_KEY];
};

// A rule carries at most one of min/max. Overrides can be authored outside this
// app, and formatISODuration throws a RangeError on a malformed duration.
export const formatOverrideDuration = (override: RetentionOverrideInput) => {
  const duration = override?.minDuration || override?.maxDuration || '';
  try {
    return formatISODuration(duration);
  } catch {
    return duration;
  }
};
